import LoadingModal from 'components/elements/modal/LoadingModal'
import Assigned from 'components/pages/Assigned'
import Canceled from 'components/pages/Canceled'
import Closed from 'components/pages/Closed'
import Confirmation from 'components/pages/Confirmation'
import Start from 'components/pages/Start'
import Error from 'components/pages/Error'
import Impasse from 'components/pages/Impasse'
import Informed from 'components/pages/Informed'
import Joined from 'components/pages/Joined'
import LocationSharing from 'components/pages/LocationSharing'
import NoGuide from 'components/pages/NoGuide'
import ReAssigned from 'components/pages/ReAssigned'
import Reservation from 'components/pages/Reservation'
import Reserved from 'components/pages/Reserved'
import ServiceEmptyFuel from 'components/pages/ServiceEmptyFuel'
import ServiceInformation from 'components/pages/ServiceInformation'
import ServiceOtherEmptyFuel from 'components/pages/ServiceOtherEmptyFuel'
import SpecialContract from 'components/pages/SpecialContract'
import TroublePlace from 'components/pages/TroublePlace'
import VehicleState from 'components/pages/TroublePlace/Info/VehicleState'
import Accident from 'components/pages/TroublePlace/Info/VehicleState/Accident'
import EmptyFuel from 'components/pages/TroublePlace/Info/VehicleState/EmptyFuel'
import EngineTrouble from 'components/pages/TroublePlace/Info/VehicleState/EngineTrouble'
import FallingWheel from 'components/pages/TroublePlace/Info/VehicleState/FallingWheel'
import FlatTire from 'components/pages/TroublePlace/Info/VehicleState/FlatTire'
import LossKey from 'components/pages/TroublePlace/Info/VehicleState/LossKey'
import Others from 'components/pages/TroublePlace/Info/VehicleState/Others'
import TransportDestination from 'components/pages/TroublePlace/Info/VehicleState/TransportDestination'
import Surrounding from 'components/pages/TroublePlace/Surrounding'
import ContractInfo from 'components/pages/ContractInfo'
import { API } from 'constants/api'
import { PAGE_PATH } from 'constants/pagePath'
import { WEBSOCKET } from 'constants/websocket'
import React, { useState } from 'react'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { QnState } from 'states/QnState'
import styled from 'styled-components'
import {
  useCloneState,
  useControlQNStatus,
  useEditingItemStateKey,
  useJwt,
  useQNStateKey,
} from 'utils/commonHooks'
import { useWebsocket } from 'utils/websocket'
import { useSender } from './utils/axios'
import ReAccess from 'components/pages/ReAccess'

/**
 * Appコンポーネント
 * ルーティング設定とコンポーネントの紐付け
 */
const App: React.FC = () => {
  const qnStates = useCloneState(useQNStateKey, QnState)
  const { state, setState } = qnStates

  const [isJwtLoaded, setIsJwtLoaded] = useState<boolean>(false)

  useJwt(setIsJwtLoaded)
  useWebsocket(WEBSOCKET.MAIN, qnStates)
  const landingStatus = useControlQNStatus(qnStates, isJwtLoaded)
  const location = useLocation()
  const history = useHistory()

  const showLoadingModal =
    location.pathname !== PAGE_PATH.ERROR &&
    !landingStatus.apiCalled &&
    !(!landingStatus.jwt.length && isJwtLoaded)
  const [, getMaintenanceStatus] = useSender(API.MAINTENANCE)

  /** メンテナンス中か判定しメンテナンス画面に遷移するEffect */
  React.useEffect(() => {
    const { client, from, line, params } = state.urlParameters
    if (client && from && line) {
      // メンテナンスステータスを取得する
      ;(async (): Promise<void> => {
        /** メンテナンスステータス */
        const block = history.block(() => undefined)
        // resultStatusが1以上の場合はメンテナンス中のため、メンテナンス画面に遷移する
        getMaintenanceStatus({}).then((resultStatus) => {
          if (resultStatus) {
            // TODO: resultStatus.data.data.statusではエラーが発生するため、一旦下記の状態に変更する
            if (Number(resultStatus.data?.data?.status) > 0) {
              /** QNメンテナンス画面に遷移 */

              /** QNのパラメータ */
              const searchParams = new URLSearchParams(
                params
                  ? {
                      client,
                      from,
                      line,
                      params,
                    }
                  : {
                      client,
                      from,
                      line,
                    }
              ).toString()

              /** ローカルストレージ削除 */
              window.localStorage.removeItem(useQNStateKey)
              window.localStorage.removeItem(useEditingItemStateKey)

              const maintenanceDomain =
                process.env.NODE_ENV === 'development'
                  ? `http://${window.location.hostname}:3001` // 開発モードでメンテナンス画面に遷移したい場合、rak-front-maintenanceのポートを3001で起動すること
                  : `${window.location.origin}/maintenance/index.html`

              window.location.href = `${maintenanceDomain}?${searchParams}`

              return false // 画面制御ブロック
            }
            return block
          }
        })
      })()
    }
  }, [
    location.pathname,
    state.urlParameters.client,
    state.urlParameters.from,
    state.urlParameters.line,
    state.urlParameters.params,
  ])

  /**
   *  画面遷移時に今いる画面のパスをQNStateに保存する
   *  復帰画面の「続きから入力する」押下のときに、離脱前の画面のパスを取得するため
   */
  React.useEffect(() => {
    ;((): void => {
      if (
        history.location.pathname === PAGE_PATH.RE_ACCESS ||
        state.qnStatus !== 'FILLING'
      ) {
        // 復帰画面上でリロードしたもしくは、完了画面以降の場合は、stateに離脱前の画面のパスを格納せずにそのままreturnする
        return
      }
      setState({
        ...state,
        currentInputPagePath: history.location.pathname,
      })
    })()
  }, [history.location.pathname, state.qnStatus])

  return (
    <React.Fragment>
      {/* QNステータス受信待ちの間は画面を白くする */}
      <LoadingBackground visible={showLoadingModal}>
        <LoadingModal on={showLoadingModal} />
      </LoadingBackground>
      <Switch>
        {/* 本番画面 */}
        <Route path={PAGE_PATH.START} exact>
          <Start />
        </Route>
        <Route path={PAGE_PATH.TROUBLE_PLACE} exact>
          <TroublePlace {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.JOINED} exact>
          <Joined {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.CANCELED} exact>
          <Canceled {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.ERROR}>
          <Error errorCode={state.errorCode} />
        </Route>
        <Route path={PAGE_PATH.CLOSED} exact>
          <Closed {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.INFORMED} exact>
          <Informed {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.NO_GUIDE} exact>
          <NoGuide {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.RE_ASSIGNED} exact>
          <ReAssigned {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.IMPASSE} exact>
          <Impasse {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.RESERVED} exact>
          <Reserved {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.ASSIGNED} exact>
          <Assigned {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.TROUBLE_PLACE_SURROUNDING} exact>
          <Surrounding {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.TRANSPORT_DESTINATION} exact>
          <TransportDestination {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.SPECIAL_CONTRACT} exact>
          <SpecialContract {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.CONFIRMATION} exact>
          <Confirmation {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.RESERVATION} exact>
          <Reservation {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.LOCATION_SHARING} exact>
          <LocationSharing {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.SERVICE_EMPTY} exact>
          <ServiceEmptyFuel {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.SERVICE_OTHER_EMPTY} exact>
          <ServiceOtherEmptyFuel {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.SERVICE_INFORMATION} exact>
          <ServiceInformation />
        </Route>
        <Route path={PAGE_PATH.CONTRACT_INFORMATION} exact>
          <ContractInfo {...qnStates} />
        </Route>

        <Route path={PAGE_PATH.RE_ACCESS} exact>
          <ReAccess {...qnStates} />
        </Route>

        {/* QN-03: 車の状況 */}
        <Route path={PAGE_PATH.VEHICLE_STATE} exact>
          <VehicleState {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.ENGINE_TROUBLE} exact>
          <EngineTrouble {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.FLAT_TIRE} exact>
          <FlatTire {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.ACCIDENT} exact>
          <Accident {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.FALLING_WHEEL} exact>
          <FallingWheel {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.EMPTY_FUEL} exact>
          <EmptyFuel {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.LOSS_KEY} exact>
          <LossKey {...qnStates} />
        </Route>
        <Route path={PAGE_PATH.OTHERS} exact>
          <Others {...qnStates} />
        </Route>

        {/* 存在しないルートは全て共通エラー画面を表示 */}
        <Route path="">
          <Error errorCode={state.errorCode} />
        </Route>
      </Switch>
    </React.Fragment>
  )
}

const LoadingBackground = styled.div<{
  visible?: boolean
}>`
  position: fixed;
  z-index: 10000;
  width: 100vw;
  height: 100vh;
  background: var(--color-main-gradient-start);
  background: linear-gradient(
    347deg,
    var(--color-main-gradient-start),
    var(--color-main-gradient-end)
  );
  ${(props): string => (props.visible ? '' : 'display: none;')}
`

export default App
